import React from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  Modal,
  Button,
  Select,
  DatePickerField,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from './styled';

const selectProps = {
  styles: {
    container: provided => ({
      ...provided,
      width: '100%',
    }),
    menu: provided => ({
      ...provided,
      marginTop: '1px',
    }),
  },
  theme: theme => ({
    ...theme,
    borderRadius: 3,
  }),
};

type Props = {
  isOpen: boolean,
  initialClient: number,
  startDate: Date,
  clients: any[],
  workouts: any[],
  onSubmit: { (): void },
  onLogActivity: { (startDate: Date): void },
  onRequestClose: { (): void },
  onUserSelected: { (userId: number): void },
  onRequestCreateWorkout: { (): void },
};

const NewWorkoutEvent = ({
  isOpen,
  startDate,
  clients,
  workouts,
  initialClient,
  onSubmit,
  onLogActivity,
  onRequestClose,
  onUserSelected,
  onRequestCreateWorkout,
}: Props) => (
  <Modal
    title={i18n.t('schedule.new.workout')}
    isOpen={isOpen}
    contentStyle={{ width: 400 }}
    onRequestClose={onRequestClose}
  >
    <Formik
      initialValues={{
        client: initialClient,
        start: DateTime.fromJSDate(startDate),
        end: DateTime.fromJSDate(startDate).plus({ hours: 1 }),
      }}
      onSubmit={onSubmit}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        handleBlur,
      }) => (
        <Flex
          as="form"
          column
          width="100%"
          onSubmit={handleSubmit}
        >
          <Flex width="100%" justify="flex-end">
            <Button appearance="link" onClick={() => onLogActivity(startDate)}>
              Log Activity instead
            </Button>
          </Flex>
          <Flex width="100%" column>
            <S.Label htmlFor="client-select">
              {i18n.t('client')}
            </S.Label>
            <Select
              {...selectProps}
              id="client-select"
              value={clients
                ? clients.find(option => option.value === String(values.client))
                : ''}
              onChange={(option: Option) => {
                setFieldValue('client', option.value);
                setFieldValue('workout', null);
                onUserSelected(option.value);
              }}
              onBlur={handleBlur}
              name="client"
              options={clients}
            />
          </Flex>
          <Flex width="100%" column>
            <S.Label>
              Time
            </S.Label>
            <Flex spacing={10}>
              <Field
                name="start"
                dateFormat="MM/dd/yyyy   hh:mm a"
                showTimeSelect
                minDate={new Date()}
                onChange={(date) => {
                  const start = DateTime.fromJSDate(date);
                  setFieldValue('start', start);
                  setFieldValue('end', start.plus({ hours: 1 }));
                }}
                component={DatePickerField}
              />
              <Field
                name="end"
                selected={new Date(values.start.toISO())}
                dateFormat="MM/dd/yyyy   hh:mm a"
                showTimeSelect
                minDate={new Date(values.start.toISO())}
                onChange={(date) => {
                  setFieldValue('end', DateTime.fromJSDate(date));
                }}
                component={DatePickerField}
              />
            </Flex>
          </Flex>
          <Flex width="100%" column>
            <S.Label htmlFor="workout-select">
              {i18n.t('Workout')}
            </S.Label>
            <Select
              {...selectProps}
              id="workout-select"
              value={workouts ? workouts.find(option => option.value === values.workout) || '' : ''}
              onBlur={handleBlur}
              onChange={(option: Option) => setFieldValue('workout', option.value)}
              name="workout"
              options={workouts}
            />
          </Flex>
          <Flex
            width="100%"
            align="center"
            justify="space-between"
            margin="15px 0 0 0"
          >
            <Text color="#9ea0a1">
              Or you can
            </Text>
            <Button
              ghost
              appearance="primary"
              onClick={() => onRequestCreateWorkout(values)}
              isDisabled={isSubmitting}
            >
              {i18n.t('create.new.workout')}
            </Button>
          </Flex>
          <S.Footer>
            <hr />
            <Flex width="100%" justify="space-between">
              <Button
                ghost
                appearance="danger"
                onClick={onRequestClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                appearance="primary"
                isLoading={isSubmitting}
                isDisabled={
                  isSubmitting
                  || !values.start
                  || !values.end
                  || !values.workout
                  || !values.client
                }
              >
                Schedule
              </Button>
            </Flex>
          </S.Footer>
        </Flex>
      )}
    </Formik>
  </Modal>
);

export default NewWorkoutEvent;
